// [maxCode, emoji] — first match wins, covers all WMO weather interpretation codes.
// Explicit Unicode escapes prevent source-encoding issues.
// BMP chars (U+2600–U+26FF) require \uFE0F (VS-16) to force emoji presentation.
// Unicode 7.0 supplementary weather chars (U+1F32B–U+1F328) also require \uFE0F
// in several browser/OS combinations despite being above U+FFFF.
const WMO_EMOJI_MAP: [number, string][] = [
  [0, '\u2600\uFE0F'], // ☀️  clear sky
  [1, '\u26C5'], // ⛅  mainly clear
  [2, '\u26C5'], // ⛅  partly cloudy
  [3, '\u2601\uFE0F'], // ☁️  overcast
  [48, '\u{1F32B}\uFE0F'], // 🌫️  fog / rime fog    (45–48)
  [57, '\u{1F326}\uFE0F'], // 🌦️  drizzle            (51–57)
  [65, '\u{1F327}\uFE0F'], // 🌧️  rain               (61–65)
  [67, '\u{1F328}\uFE0F'], // 🌨️  freezing rain      (66–67)
  [77, '\u2744\uFE0F'], // ❄️  snow / grains      (71–77)
  [82, '\u{1F326}\uFE0F'], // 🌦️  rain showers       (80–82)
  [86, '\u{1F328}\uFE0F'], // 🌨️  snow showers       (85–86)
  [99, '\u26C8\uFE0F'], // ⛈️  thunderstorm       (95–99)
];

// Exact per-code descriptions — industry-standard terms matching major weather services.
// The WMO codes are a strict enum; a direct Record is more precise than the threshold table.
const WMO_DESCRIPTION_MAP: Record<number, string> = {
  0: 'Clear',
  1: 'Mostly clear',
  2: 'Partly cloudy',
  3: 'Cloudy',
  45: 'Foggy',
  48: 'Freezing fog',
  51: 'Light drizzle',
  53: 'Drizzle',
  55: 'Heavy drizzle',
  56: 'Light freezing drizzle',
  57: 'Freezing drizzle',
  61: 'Light rain',
  63: 'Rain',
  65: 'Heavy rain',
  66: 'Light freezing rain',
  67: 'Freezing rain',
  71: 'Light snow',
  73: 'Snow',
  75: 'Heavy snow',
  77: 'Snow grains',
  80: 'Light showers',
  81: 'Showers',
  82: 'Heavy showers',
  85: 'Light snow showers',
  86: 'Snow showers',
  95: 'Thunderstorm',
  96: 'Thunderstorm with hail',
  99: 'Severe thunderstorm',
};

function wmoCodeToEmoji(code: number): string {
  return WMO_EMOJI_MAP.find(([max]) => code <= max)?.[1] ?? '\u{1F321}'; // 🌡 fallback
}
function wmoCodeToDescription(code: number): string {
  return WMO_DESCRIPTION_MAP[code] ?? 'Unknown';
}
export function celsiusToFahrenheit(c: number): number {
  return Math.round((c * 9) / 5 + 32);
}

export type WeatherDay = { maxC: number; emoji: string; description: string };

export async function fetchWeather(
  lat: number,
  lng: number,
  startDate: string,
  endDate: string
): Promise<Record<string, WeatherDay>> {
  const url = new URL('https://api.open-meteo.com/v1/forecast');
  url.searchParams.set('latitude', String(lat));
  url.searchParams.set('longitude', String(lng));
  url.searchParams.set('daily', 'temperature_2m_max,weathercode');
  url.searchParams.set('temperature_unit', 'celsius');
  url.searchParams.set('timezone', 'auto');
  url.searchParams.set('start_date', startDate);
  url.searchParams.set('end_date', endDate);

  const res = await fetch(url.toString());
  if (!res.ok) {
    throw new Error(`Weather fetch failed: ${res.status}`);
  }

  const json: {
    daily: { time: string[]; temperature_2m_max: number[]; weathercode: number[] };
  } = await res.json();

  const result: Record<string, WeatherDay> = {};
  for (let i = 0; i < json.daily.time.length; i++) {
    const code = json.daily.weathercode[i];
    result[json.daily.time[i]] = {
      maxC: Math.round(json.daily.temperature_2m_max[i]),
      emoji: wmoCodeToEmoji(code),
      description: wmoCodeToDescription(code),
    };
  }
  return result;
}
